import React, { createContext, useContext, useMemo } from 'react';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import type {
	FacebookShareRecommendations,
	RedditShareRecommendations,
} from 'src/app/pages/starterDashboardV2/shared/hooks';
import { useShareRecommendations } from 'src/app/pages/starterDashboardV2/shared/hooks';

export type Recommendation = NonNullable<FacebookShareRecommendations[0] | RedditShareRecommendations[0]>;

type RecommendationsContextType = {
	data: {
		facebook: FacebookShareRecommendations;
		reddit: RedditShareRecommendations;
		isLoading: boolean;
		hasData: boolean;
		hasFacebookGroups: boolean;
		hasRedditGroups: boolean;
		shareRecommendations: { facebook: FacebookShareRecommendations; reddit: RedditShareRecommendations };
		shouldDisplayShareRecommendations: boolean;
		shareRecommendationsLoading: boolean;
	};
	actions: {
		getFacebookGroups: () => FacebookShareRecommendations;
		getRedditGroups: () => RedditShareRecommendations;
	};
};

const RecommendationsContext = createContext<RecommendationsContextType>({
	data: {
		facebook: [],
		reddit: [],
		isLoading: false,
		hasData: false,
		hasFacebookGroups: false,
		hasRedditGroups: false,
		shareRecommendations: { facebook: [], reddit: [] },
		shouldDisplayShareRecommendations: false,
		shareRecommendationsLoading: false,
	},
	actions: {
		getFacebookGroups: () => [],
		getRedditGroups: () => [],
	},
});

export const useRecommendations = () => useContext(RecommendationsContext);

export const RecommendationsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const {
		data: {
			petition: { slug },
		},
	} = useStarterDashboardV2PageData();

	const shareRecommendationsResult = useShareRecommendations(slug);

	const contextValue = useMemo(() => {
		const { shareRecommendations, shouldDisplayShareRecommendations, shareRecommendationsLoading } =
			shareRecommendationsResult.data;

		return {
			data: {
				facebook: shareRecommendations.facebook,
				reddit: shareRecommendations.reddit,
				isLoading: shareRecommendationsLoading,
				hasData: shouldDisplayShareRecommendations,
				hasFacebookGroups: shareRecommendations.facebook.length > 0,
				hasRedditGroups: shareRecommendations.reddit.length > 0,
				shareRecommendations,
				shouldDisplayShareRecommendations,
				shareRecommendationsLoading,
			},
			actions: {
				getFacebookGroups: () => shareRecommendations.facebook,
				getRedditGroups: () => shareRecommendations.reddit,
			},
		};
	}, [shareRecommendationsResult.data]);

	return <RecommendationsContext.Provider value={contextValue}>{children}</RecommendationsContext.Provider>;
};
